import { createMemo, createResource, createSignal, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { organization as organizationTable, user as userTable } from "@boveda/powersync";
import { buildShareSecretRequest, generateId, queryKeys } from "@boveda/shared";
import { createPowerSyncQuery } from "@boveda/shared/solid";
import { Send, ShieldCheck, Users } from "@fleet/icons";
import { api } from "@/lib/api";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { useI18n } from "@/providers/i18n-provider";
import { type DecryptedVaultItem, useVault } from "../vault-context";

interface OrgRow {
  id: string;
  name: string | null;
}

interface UserRow {
  id: string;
  name: string | null;
  email: string | null;
}

interface ShareRecipient {
  readonly userId: string;
  readonly publicKey: string;
  readonly orgName: string;
  readonly displayName: string;
}

/**
 * Share item with org peer: secret is SEALED on
 * this client to recipient public key (ECIES) and server only
 * guards/forwards opaque envelope. Potential recipients are members of
 * my orgs who already have vault account (published public key).
 */
export function ShareSecretPanel(props: { item: DecryptedVaultItem; onDone: () => void }) {
  const { t } = useI18n();
  const vault = useVault();
  const { db } = usePowerSyncContext();
  const [busy, setBusy] = createSignal(false);
  const [sent, setSent] = createSignal(false);
  const [error, setError] = createSignal("");

  // My orgs (sync only brings mine) + member names.
  const orgsQuery = createPowerSyncQuery<OrgRow>({
    queryKey: () => queryKeys.vault.localOrgs(vault.userId(), "share"),
    queryFn: () => {
      const d = vault.drizzle();
      return d
        ? d
            .select({ id: organizationTable.id, name: organizationTable.name })
            .from(organizationTable)
        : null;
    },
    db,
    enabled: () => Boolean(vault.drizzle()),
  });

  const usersQuery = createPowerSyncQuery<UserRow>({
    queryKey: () => queryKeys.vault.memberNames(`share-${vault.userId()}`),
    queryFn: () => {
      const d = vault.drizzle();
      return d
        ? d
            .select({ id: userTable.id, name: userTable.name, email: userTable.email })
            .from(userTable)
        : null;
    },
    db,
    enabled: () => Boolean(vault.drizzle()),
  });

  // Public keys per org (REST: published by server; they are
  // public by nature). Only members WITH a vault account appear.
  const [recipients] = createResource(
    () => (orgsQuery.data ?? []).map((o) => ({ id: o.id, name: o.name ?? "" })),
    async (orgs) => {
      const me = vault.userId();
      const byUser = new Map<string, ShareRecipient>();
      for (const org of orgs) {
        try {
          const { data } = await api.GET("/api/vault/org/{org_id}/member-keys", {
            params: { path: { org_id: org.id } },
          });
          for (const key of data ?? []) {
            if (key.userId !== me && !byUser.has(key.userId)) {
              byUser.set(key.userId, {
                userId: key.userId,
                publicKey: key.publicKey,
                orgName: org.name,
                displayName: key.userId,
              });
            }
          }
        } catch {
          // Org without access or offline: omitted (local-first, without breaking UI).
        }
      }
      return [...byUser.values()];
    },
  );

  const withNames = createMemo<ShareRecipient[]>(() => {
    const users = new Map((usersQuery.data ?? []).map((u) => [u.id, u]));
    return (recipients() ?? []).map((r) => {
      const u = users.get(r.userId);
      return { ...r, displayName: u?.name || u?.email || r.userId };
    });
  });

  async function send(recipient: ShareRecipient): Promise<void> {
    setBusy(true);
    setError("");
    try {
      const body = await buildShareSecretRequest({
        id: generateId(),
        fromUserId: vault.userId(),
        toUserId: recipient.userId,
        recipientPublicKey: recipient.publicKey,
        secret: props.item.plain,
      });
      const { error: apiError } = await api.POST("/api/vault/share", { body });
      if (apiError) {
        throw new Error("share request failed");
      }
      // Audit (hash-chain): 'share' event is chained. Best-effort:
      // does not block sharing if log fails to respond.
      const vid = vault.vaultId();
      if (vid) {
        await api
          .POST("/api/vault/access/log", {
            body: { vaultId: vid, action: "share", itemId: props.item.id },
          })
          .catch(() => {});
      }
      setSent(true);
    } catch {
      setError(t(msg`Could not share the item. Check your connection and try again.`));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div class="flex flex-col gap-4" data-testid="share-panel">
      <p class="text-sm text-muted-foreground">
        {t(
          msg`The item is sealed to the recipient's public key on this device. The server only relays an opaque envelope and can never read it.`,
        )}
      </p>

      <Show
        when={!sent()}
        fallback={
          <div
            class="flex items-center gap-2 rounded-lg border border-success/40 bg-success/10 px-3 py-2.5 text-sm"
            data-testid="share-sent"
          >
            <ShieldCheck class="h-4 w-4 text-success" />
            {t(msg`Shared. Only the recipient can open it.`)}
            <Button size="sm" variant="ghost" class="ml-auto" onClick={() => props.onDone()}>
              {t(msg`Done`)}
            </Button>
          </div>
        }
      >
        <Show
          when={withNames().length > 0 || recipients.loading}
          fallback={
            <EmptyState
              icon={Users}
              title={t(msg`No recipients available`)}
              description={t(
                msg`Recipients are members of your organizations who already set up their vault.`,
              )}
            />
          }
        >
          <ul class="flex flex-col gap-1.5" data-testid="share-recipient-list">
            <For each={withNames()}>
              {(recipient) => (
                <li>
                  <button
                    type="button"
                    class="flex w-full items-center gap-3 rounded-lg border border-border/50 bg-card/40 px-3 py-2.5 text-left transition-colors hover:bg-primary/5 disabled:opacity-50"
                    disabled={busy()}
                    onClick={() => void send(recipient)}
                    data-testid="share-recipient"
                  >
                    <span class="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                      <Send class="h-4 w-4" />
                    </span>
                    <span class="min-w-0 flex-1">
                      <span class="block truncate text-sm font-medium">
                        {recipient.displayName}
                      </span>
                      <span class="block truncate text-xs text-muted-foreground">
                        {recipient.orgName}
                      </span>
                    </span>
                  </button>
                </li>
              )}
            </For>
          </ul>
        </Show>

        <Show when={error()}>
          <p class="text-sm text-destructive" role="alert">
            {error()}
          </p>
        </Show>
      </Show>
    </div>
  );
}
